import clsx from 'clsx';
import React, { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import { ScrollToTop } from '../../../common/table/helper';
import { AppointmentColorSettingsRuleDto } from '@lmt/shared-types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';

import styles from './ColorRulesTable.module.scss';
import { ReactComponent as InfoIcon } from '@lmt/unicorn-shared/app/icons/info.svg';
import { ReactComponent as DragAndDropIcon } from '@lmt/unicorn-shared/app/icons/drag-and-drop.svg';
import {
  getBGColor,
  getFontColor,
  getHexColorName,
} from '../components/color-parser';
import { ConditionParser } from './components/ConditionParser';
import i18next from 'i18next';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { useUniSelector } from '@lmt/unicorn-shared/app/store/types';
import { getSettings } from '@lmt/unicorn-shared/app/features/settings/selectors';
import { TableDotMenu } from '../../../common/table/TableDotMenu';

const DragHandle = React.forwardRef<HTMLDivElement>((props, ref) => (
  <div
    {...props}
    ref={ref}
    title={i18next.t('calendar.color_settings.list.drag_and_drop_tooltip')}
    data-test-id="drag-and-drop-button"
  >
    <DragAndDropIcon className={styles.icon} />
  </div>
));

const SortableRowWrapper = (props: RowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.data.position });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <Row
      {...props}
      {...attributes}
      listeners={listeners}
      style={style}
      ref={setNodeRef}
    />
  );
};

const Row = React.forwardRef<HTMLDivElement, RowProps>((props, ref) => {
  const { data, editRule, removeRule, isDefault, style, listeners, ...rest } =
    props;

  const i18n = useScopedTranslation('calendar.color_settings.list');
  const isNightTheme = useUniSelector(getSettings).isNightTheme;
  const color = getBGColor(data.color, isNightTheme);
  return (
    <div
      {...rest}
      style={style}
      className={clsx(styles.row, styles.rowData)}
      onClick={() => editRule(data)}
      data-test-id="colour-style-rule"
    >
      <div>{!isDefault && <DragHandle ref={ref} {...listeners} />}</div>
      <div className={styles.colorContainer} data-test-id="colour-column">
        <div
          className={styles.color}
          style={{
            backgroundColor: color,
            color: getFontColor(color),
          }}
          data-test-id="style-colour"
        >
          {getHexColorName(data.color)}
        </div>
      </div>
      <div>
        <ConditionParser condition={data.filterExpression} />
      </div>
      <div
        className={styles.optionCell}
        onClick={(event: any) => event.stopPropagation()}
      >
        <TableDotMenu
          testId="three-dots-menu"
          options={[
            {
              title: i18n.t('options.edit_button'),
              onClick: () => editRule(data),
            },
            {
              title: i18n.t('options.delete_button'),
              onClick: () => removeRule(data),
              isDeleteItem: true,
              isUnderLine: true,
              isHidden: data.isDefault,
            },
          ]}
        />
      </div>
    </div>
  );
});

type Props = {
  isNoRulesFound: boolean;
  data: AppointmentColorSettingsRuleDto[];
  isDefault: boolean;
  editRule: (rule: AppointmentColorSettingsRuleDto) => void;
  removeRule: (rule: AppointmentColorSettingsRuleDto) => void;
  onSortEnd: (event: DragEndEvent) => void;
};
type RowProps = {
  listeners?: SyntheticListenerMap;
  index: number;
  data: AppointmentColorSettingsRuleDto;
  isDefault: boolean;
  editRule: (rule: AppointmentColorSettingsRuleDto) => void;
  removeRule: (rule: AppointmentColorSettingsRuleDto) => void;
  style?: CSSProperties;
};

export const ColorRulesTable: FC<Props> = React.memo(
  function ColorRulesTable(props) {
    const i18next = useScopedTranslation('calendar.color_settings.list');
    const tableRef = useRef<HTMLDivElement>(null);
    const sensors = useSensors(useSensor(PointerSensor));
    const [activeId, setActiveId] = useState<number | null>(null);

    useEffect(() => {
      if (tableRef.current !== null) {
        ScrollToTop(tableRef.current);
      }
    }, [props.data]);

    if (props.isNoRulesFound) {
      return (
        <div className={styles.emptyList}>
          <div className={styles.emptyListText}>
            {i18next.t('empty_list_text')}
          </div>
        </div>
      );
    }

    return (
      <>
        <div ref={tableRef}>
          <div className={clsx(styles.header, styles.row)}>
            <div className={styles.info} title={i18next.t('info_tooltip')}>
              {!props.isDefault && <InfoIcon className={styles.infoIcon} />}
            </div>
            <div className={styles.headerCell}>
              {i18next.t('columns.color')}
            </div>
            <div className={styles.headerCell}>
              {i18next.t('columns.condition')}
            </div>
            <div></div>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e) => {
              setActiveId(e.active.id as number);
            }}
            onDragEnd={(e) => {
              props.onSortEnd(e);
              setActiveId(null);
            }}
          >
            <div>
              <SortableContext
                items={props.data.map((x, i) => x.position)}
                strategy={verticalListSortingStrategy}
              >
                {props.data.map((row, index) => (
                  <SortableRowWrapper
                    index={index}
                    key={row.position}
                    data={row}
                    isDefault={props.isDefault}
                    editRule={props.editRule}
                    removeRule={props.removeRule}
                  />
                ))}
              </SortableContext>
            </div>
          </DndContext>
        </div>
      </>
    );
  },
);
